<template>
  <div class="plan-revisions">
    <a-divider>
      История версий
      <a-tag
        v-if="!loading"
        :style="{ marginLeft: '8px' }">
        {{ totalCount }}
      </a-tag>
    </a-divider>

    <a-tabs
      v-model:activeKey="activeTab"
      size="small">
      <a-tab-pane
        v-for="tab in tabs"
        :key="tab.key"
        :tab="`${tab.title} (${tabsData[tab.key].data.length})`" />
    </a-tabs>

    <a-skeleton
      v-if="loading"
      active
      :paragraph="{ rows: 5 }" />

    <template v-else>
      <a-alert
        v-if="hasSavesAfterRender"
        type="warning"
        show-icon
        message="Есть сохранения после рендера. Они не вошли в виджет"
        :style="{ marginBottom: '1rem' }" />
      <a-alert
        v-if="!revisions.length && fallback"
        type="info"
        show-icon
        :style="{ marginBottom: '1rem' }">
        <template #message>
          История ещё не велась: следующее сохранение создаст первую ревизию.
        </template>
        <template #description>
          Текущий файл: <code>{{ fallback.path }}</code>,
          {{ formatSize(fallback.size) }}, изменён {{ formatTimestamp(fallback.updated_at) }}.
        </template>
      </a-alert>

      <a-empty
        v-if="!revisions.length && !fallback"
        :image="simpleImage"
        description="Нет сохранённых ревизий" />

      <a-table
        v-if="revisions.length"
        :columns="columns"
        :data-source="revisions"
        :pagination="false"
        row-key="id"
        size="small">
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'id'">
            <a-tooltip :title="record.id === revisions[0].id ? 'Актуальная' : null">
              <a-tag :color="record.id === revisions[0].id ? 'green' : 'default'">
                #{{ revisionNumber(record) }}
              </a-tag>
            </a-tooltip>
          </template>

          <template v-if="column.key === 'renders'">
            <RevisionWidgets :renders="record.renders ?? []" />
          </template>

          <template v-if="column.key === 'created_at'">
            {{ formatDate(record.created_at) }}
          </template>

          <template v-if="column.key === 'author'">
            {{ record.author ? `${record.author.id}: ${record.author.name}` : 'Неавторизованный' }}
          </template>

          <template v-if="column.key === 'diff'">
            <span
              v-if="record.lines_added !== null || record.lines_removed !== null"
              class="revision-diff">
              <span class="revision-diff__added">+{{ record.lines_added ?? 0 }}</span>
              <span class="revision-diff__removed">−{{ record.lines_removed ?? 0 }}</span>
            </span>
            <span v-else>—</span>
          </template>

          <template v-if="column.key === 'size'">
            {{ formatSize(record.size) }}
          </template>

          <template v-if="column.key === 'label'">
            <span
              v-if="!record.label"
              class="revision-label-empty">Без названия</span>
            <a-typography-text
              :content="record.label || ''"
              :editable="{ onChange: (value) => saveLabel(record, value) }" />
          </template>

          <template v-if="column.key === 'actions'">
            <a-button
              type="link"
              size="small"
              :loading="downloadingId === record.id"
              @click="downloadRevision(record)">
              Скачать
            </a-button>
            <a-button
              v-if="editorLink"
              type="link"
              size="small"
              :href="revisionEditorLink(record)"
              target="_blank">
              Открыть в редакторе
            </a-button>
          </template>
        </template>
      </a-table>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { Empty, message } from 'ant-design-vue';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { api } from '@/api';
import RevisionWidgets from '@/Components/RevisionWidgets.vue';

const props = defineProps({
  planId: {
    type: String,
    default: null,
  },
  crmPlanId: {
    type: Number,
    default: null,
  },
  links: {
    type: Object,
    default: null,
  },
});

const tabs = [
  { key: 'neural', title: 'Конструктор (Neural)' },
  { key: 'furniture', title: 'Конструктор (Furniture)' },
  { key: 'svg', title: 'SVG' },
];

const columns = [
  { key: 'id', title: 'Ревизия', width: 100 },
  { key: 'renders', title: 'Виджеты' },
  { key: 'created_at', title: 'Дата', width: 160 },
  { key: 'author', title: 'Автор' },
  { key: 'diff', title: 'Изменения', width: 110 },
  { key: 'size', title: 'Размер', width: 100 },
  { key: 'label', title: 'Название' },
  { key: 'actions', title: '', width: 250 },
];

const getBlankTabsData = () => ({
  neural: { data: [], fallback: null },
  furniture: { data: [], fallback: null },
  svg: { data: [], fallback: null },
});

const simpleImage = Empty.PRESENTED_IMAGE_SIMPLE;

const activeTab = ref('neural');
const loading = ref(false);
const downloadingId = ref(null);
const tabsData = ref(getBlankTabsData());

const revisions = computed(() => tabsData.value[activeTab.value].data);
const hasSavesAfterRender = computed(() => {
  const renders = revisions.value.flatMap((revision) => (revision.renders ?? [])
    .filter((render) => render.association === 'explicit')
    .map((render) => ({ revisionId: revision.id, finishedAt: render.finished_at ?? '' })));
  renders.sort((a, b) => b.finishedAt.localeCompare(a.finishedAt) || b.revisionId - a.revisionId);
  return renders.length > 0 && revisions.value.some((revision) => revision.id > renders[0].revisionId);
});
const fallback = computed(() => tabsData.value[activeTab.value].fallback);
const totalCount = computed(() => tabs.reduce((sum, { key }) => sum + tabsData.value[key].data.length, 0));

const withMode = (link, mode) => {
  const [base, query = ''] = link.split('?');
  const params = new URLSearchParams(query);
  params.set('mode', mode);

  return `${base}?${params.toString()}`;
};

const editorLink = computed(() => {
  const constructorLink = props.links?.neural?.editor ?? null;

  if (activeTab.value === 'svg') return constructorLink ? withMode(constructorLink, 'svg') : null;

  return props.links?.[activeTab.value]?.editor ?? null;
});

const formatDate = (value) => moment(value).format('DD.MM.YYYY HH:mm:ss');

const formatTimestamp = (value) => moment.unix(value).format('DD.MM.YYYY HH:mm:ss');

const formatSize = (bytes) => {
  if (!bytes) return '0 KB';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const revisionEditorLink = (revision) => `${editorLink.value}&revision_id=${revision.id}`;

const revisionNumber = (revision) => {
  const index = revisions.value.findIndex(({ id }) => id === revision.id);

  return index < 0 ? null : revisions.value.length - index;
};

const tabByGroup = (group) => {
  if (group.kind === 'svg') return 'svg';
  if (group.type === 'neural' || group.type === 'furniture') return group.type;
  return null;
};

const loadRevisions = async () => {
  loading.value = true;
  try {
    const request = props.crmPlanId
      ? api.getCrmPlanRevisions(props.crmPlanId, {})
      : api.getServicePlanRevisions(props.planId, {});

    const { data } = await request;
    const loaded = getBlankTabsData();

    (data.groups ?? []).forEach((group) => {
      const tab = tabByGroup(group);
      if (tab) loaded[tab] = { data: group.data ?? [], fallback: group.fallback ?? null };
    });

    tabsData.value = loaded;
  } catch (error) {
    console.error('Ошибка загрузки истории версий:', error);
    message.error('Не удалось загрузить историю версий');
  } finally {
    loading.value = false;
  }
};

const downloadRevision = async (revision) => {
  downloadingId.value = revision.id;

  try {
    const request = props.crmPlanId
      ? api.getCrmPlanRevision(props.crmPlanId, revision.id)
      : api.getServicePlanRevision(props.planId, revision.id);

    const { data } = await request;

    if (!data.content) {
      message.error('Содержимое ревизии недоступно');
      return;
    }

    const planKey = props.crmPlanId ?? props.planId;
    const blob = new Blob([data.content], { type: 'application/json' });

    saveAs(blob, `plan-${planKey}-${revision.type}-rev${revisionNumber(revision)}.json`);
  } catch (error) {
    console.error('Ошибка скачивания ревизии:', error);
    message.error('Не удалось скачать ревизию');
  } finally {
    downloadingId.value = null;
  }
};

const saveLabel = async (revision, label) => {
  const previousLabel = revision.label;
  revision.label = label || null;

  try {
    if (props.crmPlanId) {
      await api.updateCrmPlanRevision(props.crmPlanId, revision.id, { label: revision.label });
    } else {
      await api.updateServicePlanRevision(props.planId, revision.id, { label: revision.label });
    }
  } catch (error) {
    revision.label = previousLabel;
    console.error('Ошибка сохранения названия ревизии:', error);
    message.error('Не удалось сохранить название ревизии');
  }
};

onMounted(() => {
  loadRevisions();
});

defineExpose({
  loadRevisions,
});
</script>

<style scoped>
.plan-revisions {
  width: 100%;
}

.revision-diff {
  display: inline-flex;
  gap: 6px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  font-weight: 600;
}

.revision-diff__added {
  color: #0b6b28;
}

.revision-diff__removed {
  color: #b3120c;
}

.revision-label-empty {
  margin-right: 6px;
  color: rgba(0, 0, 0, 0.45);
}
</style>
